// ---------------------------------------------------------------------------
// EvaluateJobsRail — the right-hand "Jobs" sidebar for the agent Evaluate tab.
//
// Lists the backing template's runs with a status badge; completed runs are
// clickable (open the scorecard), active runs show a spinner, and cancellable
// runs expose a cancel button. Purely presentational — all state + actions are
// owned by AgentEvaluateTab and passed in.
// ---------------------------------------------------------------------------

import {
  Badge,
  Body1,
  Button,
  Spinner,
  Tooltip,
  mergeClasses,
} from '@fluentui/react-components';
import {
  Dismiss20Filled,
  Dismiss20Regular,
  bundleIcon,
} from '@fluentui/react-icons';
import type { ReactNode } from 'react';

import {
  isActiveState,
  isCancellableState,
  type EvaluationRun,
  type RunState,
} from '../../../../types/evaluationRun.js';

import { useStyles } from './EvaluateJobsRail.style.js';

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
const CancelIcon = bundleIcon(Dismiss20Filled, Dismiss20Regular);
/* eslint-enable @typescript-eslint/no-unsafe-assignment */

function stateBadgeColor(
  state: RunState,
): 'informative' | 'success' | 'danger' | 'warning' | 'subtle' {
  switch (state) {
    case 'pending':
    case 'running':
      return 'informative';
    case 'cancelling':
      return 'warning';
    case 'cancelled':
      return 'subtle';
    case 'completed':
      return 'success';
    case 'failed':
      return 'danger';
  }
}

function stateLabel(state: RunState): string {
  switch (state) {
    case 'pending':
      return 'Queued';
    case 'running':
      return 'Running';
    case 'cancelling':
      return 'Cancelling';
    case 'cancelled':
      return 'Cancelled';
    case 'completed':
      return 'Completed';
    case 'failed':
      return 'Failed';
  }
}

function formatHistoryDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export interface EvaluateJobsRailProps {
  runHistory: EvaluationRun[];
  loading: boolean;
  error: string | null;
  headerError?: string | null;
  emptyMessage?: ReactNode;
  /** The run currently being cancelled (its cancel button is suppressed). */
  cancellingId: string | null;
  onCancelRun: (runId: string) => void;
  onOpenResult: (runId: string) => void;
}

export function EvaluateJobsRail({
  runHistory,
  loading,
  error,
  headerError = null,
  emptyMessage,
  cancellingId,
  onCancelRun,
  onOpenResult,
}: EvaluateJobsRailProps) {
  const styles = useStyles();

  return (
    <aside className={styles.jobsColumn} aria-label="Jobs">
      <div className={styles.jobsHeader}>
        <div className={styles.jobsTitleRow}>
          <span className={styles.jobsTitle}>Jobs</span>
          {runHistory.length > 0 && (
            <span className={styles.jobsCount}>
              {String(runHistory.length)} total
            </span>
          )}
        </div>
        {headerError !== null && (
          <Body1 className={styles.errorText} role="alert">
            {headerError}
          </Body1>
        )}
      </div>

      {loading && runHistory.length === 0 ? (
        <div className={styles.historyEmpty}>Loading runs…</div>
      ) : error !== null ? (
        <div className={styles.historyEmpty}>
          <Body1 className={styles.errorText} role="alert">
            {error}
          </Body1>
        </div>
      ) : runHistory.length === 0 ? (
        <div className={styles.historyEmpty}>
          {emptyMessage ?? (
            <>
              No evaluations yet. Select a grader and click{' '}
              <em>Run evaluation</em> to start one.
            </>
          )}
        </div>
      ) : (
        <div className={styles.historyList}>
          {runHistory.map((r) => {
            const isActive = isActiveState(r.runState);
            const canCancel =
              isCancellableState(r.runState) && cancellingId !== r.id;
            const isCompleted = r.runState === 'completed';
            const rowInner = (
              <>
                {isActive && <Spinner size="tiny" />}
                <Badge
                  appearance="tint"
                  color={stateBadgeColor(r.runState)}
                  size="small"
                >
                  {stateLabel(r.runState)}
                </Badge>
                <span className={styles.historyId}>{r.id}</span>
                <span className={styles.historyDate}>
                  {formatHistoryDate(r.systemProperties.createdDate)}
                </span>
                {canCancel && (
                  <Tooltip content="Cancel run" relationship="label">
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<CancelIcon />}
                      onClick={() => onCancelRun(r.id)}
                      aria-label="Cancel run"
                    />
                  </Tooltip>
                )}
              </>
            );
            return isCompleted ? (
              <button
                type="button"
                key={r.id}
                className={mergeClasses(
                  styles.historyRow,
                  styles.historyRowButton,
                )}
                onClick={() => onOpenResult(r.id)}
                aria-label={`View results for run ${r.id}`}
              >
                {rowInner}
              </button>
            ) : (
              <div className={styles.historyRow} key={r.id}>
                {rowInner}
              </div>
            );
          })}
        </div>
      )}
    </aside>
  );
}
